import type { VehicleInput } from '@polyloop/sim';

/**
 * Touch input — virtual stick (left) + throttle/brake (right) + drift/boost.
 * Renders its own DOM overlay (no React for the spike — just elements).
 *
 * Returned via `augment(input)` after keyboard.read, so keyboard wins on
 * desktop and touch wins on mobile where keyboard is idle.
 */
export class TouchInput {
  private steerEl?: HTMLDivElement;
  private knobEl?: HTMLDivElement;
  private gasEl?: HTMLButtonElement;
  private brakeEl?: HTMLButtonElement;
  private driftEl?: HTMLButtonElement;
  private boostEl?: HTMLButtonElement;

  private steerActiveId: number | null = null;
  private steerCenter: { x: number; y: number } = { x: 0, y: 0 };
  private steerRadius = 1;
  private steerValue = 0; // -1..+1

  private gas = false;
  private brake = false;
  private drift = false;
  private boost = false;

  attach(): void {
    // If this is a desktop+mouse-only device, still attach (cheap) but visuals are hidden by CSS.
    const steer = el('div', 'touch touch-steer');
    const knob = el('div', 'knob');
    steer.appendChild(knob);
    document.body.appendChild(steer);
    this.steerEl = steer;
    this.knobEl = knob;

    const stack = el('div', 'touch touch-btn-stack');
    document.body.appendChild(stack);
    this.driftEl = btn('DRIFT');
    this.boostEl = btn('BOOST');
    stack.appendChild(this.boostEl);
    stack.appendChild(this.driftEl);

    const throttle = el('div', 'touch touch-throttle');
    document.body.appendChild(throttle);
    this.gasEl = btn('GAS');
    this.gasEl.classList.add('gas');
    this.brakeEl = btn('BRK');
    throttle.appendChild(this.gasEl);
    throttle.appendChild(this.brakeEl);

    steer.addEventListener('pointerdown', this.onSteerDown);
    window.addEventListener('pointermove', this.onSteerMove);
    window.addEventListener('pointerup', this.onSteerUp);
    window.addEventListener('pointercancel', this.onSteerUp);

    this.bindBtn(this.gasEl, (down) => (this.gas = down));
    this.bindBtn(this.brakeEl, (down) => (this.brake = down));
    this.bindBtn(this.driftEl, (down) => (this.drift = down));
    this.bindBtn(this.boostEl, (down) => (this.boost = down));
  }

  detach(): void {
    this.steerEl?.remove();
    this.gasEl?.parentElement?.remove();
    this.driftEl?.parentElement?.remove();
    window.removeEventListener('pointermove', this.onSteerMove);
    window.removeEventListener('pointerup', this.onSteerUp);
    window.removeEventListener('pointercancel', this.onSteerUp);
  }

  augment(out: VehicleInput): void {
    if (Math.abs(this.steerValue) > 0.02) out.steer = this.steerValue;
    if (this.gas) out.throttle = 1;
    else if (this.brake) out.throttle = -1;
    if (this.drift) out.drift = true;
    if (this.boost) out.boost = true;
  }

  private onSteerDown = (e: PointerEvent): void => {
    if (!this.steerEl) return;
    const rect = this.steerEl.getBoundingClientRect();
    this.steerCenter = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    this.steerRadius = rect.width / 2;
    this.steerActiveId = e.pointerId;
    e.preventDefault();
    this.updateSteer(e.clientX, e.clientY);
  };
  private onSteerMove = (e: PointerEvent): void => {
    if (this.steerActiveId !== e.pointerId) return;
    this.updateSteer(e.clientX, e.clientY);
  };
  private onSteerUp = (e: PointerEvent): void => {
    if (this.steerActiveId !== e.pointerId) return;
    this.steerActiveId = null;
    this.steerValue = 0;
    if (this.knobEl) this.knobEl.style.transform = `translate(0,0)`;
  };

  private updateSteer(x: number, y: number): void {
    const dx = x - this.steerCenter.x;
    const dy = y - this.steerCenter.y;
    const dist = Math.hypot(dx, dy);
    const max = this.steerRadius * 0.7;
    const clamped = dist > max ? max : dist;
    const ang = Math.atan2(dy, dx);
    const kx = Math.cos(ang) * clamped;
    const ky = Math.sin(ang) * clamped;
    if (this.knobEl) this.knobEl.style.transform = `translate(${kx}px, ${ky}px)`;
    this.steerValue = clamp(kx / max, -1, 1);
  }

  private bindBtn(elm: HTMLElement, cb: (down: boolean) => void): void {
    const down = (e: Event) => {
      e.preventDefault();
      elm.classList.add('active');
      cb(true);
    };
    const up = (e: Event) => {
      e.preventDefault();
      elm.classList.remove('active');
      cb(false);
    };
    elm.addEventListener('pointerdown', down);
    elm.addEventListener('pointerup', up);
    elm.addEventListener('pointercancel', up);
    elm.addEventListener('pointerleave', up);
  }
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
}

function btn(label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = 'touch-btn';
  b.textContent = label;
  b.type = 'button';
  return b;
}
